import i18n from "i18n";
import React from "react";
import { useQueryClient } from "react-query";
import { Trans, useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";

import { Project } from "types";
import { useClient } from "utils/client";
import { Loading } from "components/Loading/Loading";
import { Directive } from "pages/Backoffice/Questionaire/types";
import { PrimaryButton } from "components/Button/PrimaryButton";
import { useProjectDetails } from "pages/Details/hooks/useProjectDetails";

interface DirectiveSearchRouteParams {
  projectId: string;
}

export const RohsResearch = () => {
  const client = useClient();
  const history = useHistory();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { directive } = useParams<{ directive: Directive }>();
  const langCode = i18n.language.split("-")[0].toUpperCase();

  const { projectId } = useParams<DirectiveSearchRouteParams>();
  const { project, fetchInProgress } = useProjectDetails(projectId);

  React.useState(false);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleSubmit = React.useCallback(async () => {
    setIsSubmitting(true);
    try {
      await client.put(`/projects/${projectId}`, {
        payload: {
          ...project,
          directives: project!.directives.map((d) => {
            if (d.name === directive) {
              return {
                ...d,
                is_exit: false,
                completed_research: true,
              };
            }

            return d;
          }),
        } as Project,
      });

      await queryClient.refetchQueries(["projects", projectId]);
    } catch (error) {
      console.error("Error submitting RoHS research:", error);
    } finally {
      setIsSubmitting(false);
      history.push(`/projects/${projectId}?saved=true`);
    }
  }, [client, projectId, project, directive, queryClient, history]);

  if (fetchInProgress) {
    return <Loading />;
  }

  return (
    <div className="questionaire max-w-4xl mx-auto px-4 py-8">
      <div className="bg-white shadow-md rounded-lg p-8">
        <h1 className="text-3xl font-bold mb-6">
          {t("steps.questionaire.rohs.research.headline")}
        </h1>

        <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-8">
          <p className="text-lg mb-4">
            <Trans i18nKey="steps.questionaire.rohs.research.message">
              You selected <strong>RoHS</strong>. Please visit the official
              EUR-LEX site to find the official directive and follow it there.
            </Trans>
          </p>
          <p className="text-base text-gray-700">
            <Trans i18nKey="steps.questionaire.rohs.research.note"></Trans>
          </p>
          <div className="mt-4">
            <a
              href={`https://eur-lex.europa.eu/legal-content/${langCode}/TXT/PDF/?uri=CELEX:32011L0065`}
              target="_blank"
              rel="noreferrer"
              className="text-blue-600 underline hover:text-blue-800"
            >
              {t("steps.questionaire.rohs.research.visit_eurlex")} →
            </a>
          </div>
        </div>

        <div className="text-right">
          <PrimaryButton onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting
              ? t("documents.download.state.loading")
              : t("steps.questionaire.rohs.research.cta")}
          </PrimaryButton>
        </div>
      </div>
    </div>
  );
};
